import React, { useEffect, useMemo, useState } from "react";
import "bootstrap-daterangepicker/daterangepicker.css";
import SurveyListHeader from "../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../component/common/layout/SurveyListSidebar";
import Svg from "../../../component/common/Svg";
import DataTable from "react-data-table-component";
import { useDispatch } from "react-redux";
import { getLeads, deleteLead, deleteBulkLeads } from "../../../store/actions/analyticsActions";
import { useSelector } from "react-redux";
import useVerifySurveyId from "../../../hooks/verifySurveyId";
import moment from "moment";
import { getStages } from "../../../store/actions/variableAction";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import SearchInput from "../../../core/Inputs/SearchInput";
import Index from "../survey/survey-list/Index";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiRequest } from "../../../services/apiService";

const ACRONYMS = new Set(["UTM", "ID", "URL", "SSO", "MFA", "IP", "BG", "HTML", "UAT", "API"]);

const toSentenceCase = (str) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word, i) => {
      if (ACRONYMS.has(word.toUpperCase())) return word.toUpperCase();
      if (i === 0) return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      return word.toLowerCase();
    })
    .join(" ");
};

/** Ellipsis + native title tooltip (avoids Blueprint/RDT flex clipping issues). */
function LeadsClipCell({ text }) {
  const raw = text == null ? "" : String(text);
  return (
    <div
      className="leads-clip-inner"
      title={raw || undefined}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        textAlign: "start",
        boxSizing: "border-box",
      }}
    >
      {raw}
    </div>
  );
}

export default function Leads() {
  //States
  const dispatch = useDispatch();
  const { leadsData } = useSelector((state) => state.analyticsData);
  const stages = useSelector((state) => state.variables?.stageData);
  const survey_id = useVerifySurveyId();
  const [searchParams, setSearchparams] = useSearchParams();
  const report_id = searchParams.get("r");
  const group_id = searchParams.get("group_id"); // Add support for group_id
  const language_code = "22";
  const { t, i18n } = useTranslation();
  const [filterText, setFilterText] = useState("");
  const [selectedDeleteLead, setSelectedDeleteLead] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [dynamicLeadFields, setDynamicLeadFields] = useState([]);
  const [activeTab, setActiveTab] = useState("all"); // "all" | "archived"

   const { userData } = useSelector((state) => state.userDataHeader)
    const isAdmin = userData?.[0]?.role_name === "Admin";
    console.log("isAdmin:", isAdmin);

   

  const getUTMInfo = (row, key) => {
    if (!row?.su_link_info) return "";

    try {
      let info = row.su_link_info;
      if (typeof info === "string") info = JSON.parse(info);
      if (typeof info === "string") info = JSON.parse(info);

      return info?.[key] ?? "";
    } catch {
      return "";
    }
  };

  const EXCLUDED_STATIC_FIELDS = [
    "HEADING",
    "DESC",
    "SUBMIT",
    "POSITION",
    "PRIVACY",
    "BANNER",
  ];

  const parseJsonSafe = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "object") return value;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const formatArchivedRuleSummary = (ruleType, ruleConfigRaw) => {
    const cfg =
      typeof ruleConfigRaw === "string"
        ? parseJsonSafe(ruleConfigRaw, {})
        : ruleConfigRaw || {};
    switch (ruleType) {
      case "field_match":
        return `"${cfg.field || ""}" ${cfg.operator || ""} "${cfg.value ?? ""}"`;
      case "age_days":
        return `Age > ${cfg.days ?? ""} day(s)`;
      case "duplicate":
        return cfg.keep === "newest"
          ? `Duplicate "${cfg.field || ""}" (archived older)`
          : `Duplicate "${cfg.field || ""}" (archived new)`;
      case "incomplete":
        return `Incomplete > ${cfg.days ?? ""} day(s)`;
      case "utm_match":
        return `${cfg.field || ""} ${cfg.operator || ""} "${cfg.value ?? ""}"`;
      default:
        return ruleType || "-";
    }
  };

  const getLeadDataObject = (row) => {
    if (!row) return {};
    return parseJsonSafe(row.lead_data || row.su_lead_info, {});
  };

  const getLeadInfo = (data, key) => {
    const value = getLeadDataObject(data);
    return value?.[key] ?? "";
  };

  const isTruthyMarketingValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;

    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return ["1", "true", "yes", "accepted", "accept", "y"].includes(normalized);
    }

    return false;
  };

  const getMarketingPrefsValue = (row) => {
    const leadData = getLeadDataObject(row);
    const rawValue =
      leadData?.TANDC ??
      leadData?.tandc ??
      leadData?.TDNC ??
      leadData?.tdnc ??
      "";

    return isTruthyMarketingValue(rawValue) ? "YES" : "NO";
  };

  const formatLeadFieldValue = (row, key) => {
    const value = getLeadInfo(row, key);
    if (key === "TANDC") {
      return getMarketingPrefsValue(row);
    }
    const field = dynamicLeadFields.find((f) => f.key === key);
    if (field?.options && value !== "" && value in field.options) {
      return field.options[value];
    }
    return value;
  };

  const sortString = (a, b) => {
    const strA = (a ?? "").toString().toLowerCase();
    const strB = (b ?? "").toString().toLowerCase();
    if (strA > strB) return 1;
    if (strB > strA) return -1;
    return 0;
  };

  const getTranslatedFieldLabel = (field) => {
    const labels = parseJsonSafe(field?.su_label_and_options, {});
    const translated = labels?.[language_code] || labels?.["22"];
    return translated || field?.su_name || "";
  };

  const isDynamicLeadField = (field) => {
    if (!field?.su_name) return false;
    if (EXCLUDED_STATIC_FIELDS.includes(field.su_name)) return false;
    if (field.su_name === "TANDC") return false;

    const fieldType = Number(field?.su_form_variable_type);
    if (![1, 3, 4].includes(fieldType)) return false;

    if (fieldType === 1) {
      const options = parseJsonSafe(field?.su_options, {});
      const firstOption = options?.["0"];
      if (firstOption?.type === "hidden") return false;
    }
    return true;
  };

  //Column and data
  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "S.No.",
        cell: (row) => <LeadsClipCell text={row.sno != null ? String(row.sno) : ""} />,
        width: "100px",
      },

      {
        name: t("analytics.date_of_survey"),
        cell: (row) => {
          const date =
            row.su_add_date_time &&
            moment(row.su_add_date_time, "DD-MM-YYYY | hh:mm A").format(
              "DD-MM-YYYY"
            );
          return <LeadsClipCell text={date || ""} />;
        },
        width: "150px",
        sortable: true,
        sortFunction: (a, b) => {
          let a1 = a.su_add_date_time && moment(a.su_add_date_time);
          let b1 = b.su_add_date_time && moment(b.su_add_date_time);
          if (a1 > b1) return 1;
          else if (a1 < b1) return -1;
          else return 0;
        },
      },
      {
        name: t("analytics.report_link"),
        cell: (row) => {
          // For groups, use survey_id and default_report from the row; for individual surveys, use params
          const useSurveyId = row.su_survey_id || survey_id;
          const useReportId = row.default_report || report_id;
          const link = `${process.env.REACT_APP_USER_LINK}/report/${useSurveyId}/${row?.su_survey_submitted_id}/${useReportId}`;
          const isDisabled = row.su_is_completed === 0 ? true : false;
          return (
            <div className="text-start w-100 leads-clip-actions">
              <button
                type="button"
                onClick={() => !isDisabled && window.open(link, "_blank")}
                className="btn-link"
                disabled={isDisabled}
                style={{
                  background: "none",
                  border: "none",
                  color: isDisabled ? "gray" : "blue",
                  textDecoration: "underline",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  verticalAlign: "bottom",
                }}
                title={
                  isDisabled
                    ? "Link disabled due to incomplete survey"
                    : link
                }
              >
                Link
              </button>
            </div>
          );
        },
        width: "200px",
      },
    ];

    const dynamicColumns = dynamicLeadFields.map((field) => ({
      name: toSentenceCase(field.label),
      cell: (row) => (
        <LeadsClipCell text={formatLeadFieldValue(row, field.key)} />
      ),
      width: "200px",
      sortable: true,
      sortFunction: (a, b) =>
        sortString(formatLeadFieldValue(a, field.key), formatLeadFieldValue(b, field.key)),
    }));

    const analyticsColumns = [
      {
        name: t("analytics.number_of_responses"),
        cell: (row) => {
          let responses = row.su_survey_response;
          let serialNo = row.su_survey_question_serial_no;
          let response = "0/0";

          if (responses && serialNo) {
            responses = responses.split("#");
            responses = responses?.filter((item) => {
              return item ? true : false;
            });
            responses = responses ? responses.length : 0;
            serialNo = serialNo.split("#")?.length ?? 1;
            response = `${responses}/${Number(serialNo - 1)}`;
          }
          return <LeadsClipCell text={response} />;
        },
        width: "200px",
      },
      {
        name: t("analytics.maturity_level"),
        cell: (row) => (
          <LeadsClipCell text={row.maturity_stage ?? ""} />
        ),
        width: "200px",
      },
      {
        name: t("analytics.total_score"),
        cell: (row) => {
          let score = row.su_survey_question_score;
          score = score?.split("#")?.reduce((total, scr) => {
            return total + Number(scr);
          }, 0);

          return <LeadsClipCell text={score != null ? String(score) : "0"} />;
        },
        width: "150px",
        sortable: true,
        sortFunction: (a, b) => {
          let a1 = a.su_survey_question_score;
          a1 = a1?.split("#")?.reduce((total, scr) => {
            return total + Number(scr);
          }, 0);
          a1 = a1 ?? 0;
          a1 = a1 + "";

          let b1 = b.su_survey_question_score;
          b1 = b1?.split("#")?.reduce((total, scr) => {
            return total + Number(scr);
          }, 0);
          b1 = b1 ?? 0;
          b1 = b1 + "";

          return sortString(a1, b1);
        },
      },
      {
        name: t("analytics.completed"),
        cell: (row) => {
          let complete = row.su_is_completed;
          complete = complete ? (Number(complete) === 1 ? "YES" : "NO") : "No";
          return <LeadsClipCell text={complete} />;
        },
        width: "150px",
        sortable: true,
        sortFunction: (a, b) => {
          let a1 = a.su_is_completed;
          a1 = a1 ? (Number(a1) === 1 ? "YES" : "NO") : "No";
          let b1 = b.su_is_completed;
          b1 = b1 ? (Number(b1) === 1 ? "YES" : "NO") : "No";
          return sortString(a1, b1);
        },
      },
      {
        name: t("analytics.unique_id"),
        cell: (row) => (
          <LeadsClipCell text={row.su_survey_submitted_id ?? ""} />
        ),
        width: "140px",
      },
      {
        name: t("analytics.utm_source") || "UTM source",
        cell: (row) => (
          <LeadsClipCell text={getUTMInfo(row, "utm_source")} />
        ),
        width: "180px",
        sortable: true,
        sortFunction: (a, b) =>
          sortString(getUTMInfo(a, "utm_source"), getUTMInfo(b, "utm_source")),
      },
      {
        name: t("analytics.utm_medium") || "UTM medium",
        cell: (row) => (
          <LeadsClipCell text={getUTMInfo(row, "utm_medium")} />
        ),
        width: "180px",
        sortable: true,
        sortFunction: (a, b) =>
          sortString(getUTMInfo(a, "utm_medium"), getUTMInfo(b, "utm_medium")),
      },
      {
        name: t("analytics.utm_campaign") || "UTM campaign",
        cell: (row) => (
          <LeadsClipCell text={getUTMInfo(row, "utm_campaign")} />
        ),
        width: "180px",
        sortable: true,
        sortFunction: (a, b) =>
          sortString(
            getUTMInfo(a, "utm_campaign"),
            getUTMInfo(b, "utm_campaign")
          ),
      },
      {
        name: t("analytics.TANDC"),
        cell: (row) => (
          <LeadsClipCell text={formatLeadFieldValue(row, "TANDC")} />
        ),
        width: "180px",
        sortable: true,
        sortFunction: (a, b) =>
          sortString(formatLeadFieldValue(a, "TANDC"), formatLeadFieldValue(b, "TANDC")),
      },
      ...(activeTab === "archived"
        ? [
            {
              name: t("analytics.archived_by_rule"),
              cell: (row) => {
                let txt = "-";
                if (row.archived_by_rule_id) {
                  if (!row.archived_rule_type) {
                    txt = `#${row.archived_by_rule_id}`;
                  } else {
                    txt = formatArchivedRuleSummary(
                      row.archived_rule_type,
                      row.archived_rule_config
                    );
                  }
                }
                return <LeadsClipCell text={txt} />;
              },
              width: "280px",
              sortable: true,
              sortFunction: (a, b) =>
                sortString(
                  formatArchivedRuleSummary(a.archived_rule_type, a.archived_rule_config),
                  formatArchivedRuleSummary(b.archived_rule_type, b.archived_rule_config)
                ),
            },
          ]
        : []),
     window.modulePermissions.M6.del === 1 && 
      { 
        name: t("survey.customVariable.action"),
        cell: (row) => (
          
          <div className="d-flex action-wrapper">
            <a
              href="#"
              data-bs-toggle="modal"
              data-bs-target="#deleteModal"
              className="action-btn"
              title={
                activeTab === "archived"
                  ? t("analytics.delete_permanently")
                  : t("survey.customVariable.action")
              }
              onClick={() => setSelectedDeleteLead(row)}
            >
              <Svg name="table-delete" />
            </a>
             
          </div>
          
        ),
      },
    ];
    const merged = [...baseColumns, ...dynamicColumns, ...analyticsColumns.filter(Boolean)];
    return merged.map((col) => ({
      ...col,
      grow: 0,
      style: {
        overflow: "hidden",
        ...(col.style || {}),
      },
    }));
  }, [dynamicLeadFields, report_id, survey_id, stages, t, activeTab]);

  const leadsTableCustomStyles = useMemo(
    () => ({
      cells: {
        style: {
          overflow: "hidden",
        },
      },
      headCells: {
        style: {
          overflow: "hidden",
        },
      },
    }),
    []
  );

  //Download CSV file
  // const downloadCSV = async (data) => {
  //   if (!data) return;

  //   data = data.map((record) => {
  //     const Id = record.su_survey_submitted_id ?? "";
  //     let leadInfo = record.lead_data && (JSON.parse(record.lead_data) ?? {});
  //     let leadData = {
  //       "First Name": leadInfo?.firstName ?? "",
  //       "Last Name": leadInfo?.lastName ?? "",
  //       Email: leadInfo?.emailAddress ?? "",
  //       Contact: leadInfo?.busPhone ?? "",
  //       Company: leadInfo?.company ?? "",
  //       "Job Function": leadInfo?.jobFunction1 ?? "",
  //       "Nature of Enquiry": leadInfo?.natureOfEnquiry ?? "",
  //     };
  //     const Link = `${process.env.REACT_APP_USER_LINK}/report/${survey_id}/${record?.su_survey_submitted_id}/1`;
  //     const Date =
  //       record.su_add_date_time &&
  //       moment(record.su_add_date_time).format("YYYY/MM/DD");

  //     let Score = record.su_survey_question_score;
  //     Score = Score?.split("#")?.reduce((total, scr) => {
  //       return total + Number(scr);
  //     }, 0);
  //     Score = Score ?? 0;

  //     const rawStageData = stages?.map((stage) => {
  //       let title = stage.su_stage_name && JSON.parse(stage.su_stage_name);
  //       title = title ? title[language_code] : "";
  //       let data =
  //         stage.su_stage_formulas && JSON.parse(stage.su_stage_formulas);
  //       const { from, to } = data ?? {};

  //       return {
  //         title,
  //         from: Number(from),
  //         to: Number(to),
  //       };
  //     });

  //     let Stage = "";
  //     rawStageData?.forEach((stage) => {
  //       if (stage.from <= Score && stage.to >= Score) {
  //         Stage = stage.title;
  //         return true;
  //       }
  //       return false;
  //     });

  //     let Completed = record.su_is_completed;
  //     Completed = Completed ? (Number(Completed) === 1 ? "YES" : "NO") : "No";

  //     let responses = record.su_survey_response;
  //     let serialNo = record.su_survey_question_serial_no;
  //     let Response = "0/0";

  //     if (responses && serialNo) {
  //       responses = responses.split("#");
  //       responses = responses?.filter((item) => {
  //         return item ? true : false;
  //       });
  //       responses = responses ? responses.length : 0;
  //       serialNo = serialNo.split("#")?.length ?? 1;
  //       Response = `${responses}/${Number(serialNo - 1)}`;
  //     }

  //     return {
  //       Id,
  //       ...leadData,
  //       Link,
  //       Date,
  //       Score,
  //       Stage,
  //       Response,
  //       Completed,
  //     };
  //   });

  //   let ws = XLSX.utils.json_to_sheet(data);
  //   let wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Leads");
  //   const wbout = await XLSX.write(wb, {
  //     type: "array",
  //     bookType: "xlsx",
  //   });

  //   const blobData = new Blob([wbout], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });
  //   FileSaver.saveAs(blobData, "Leads.xlsx");
  // };
  const downloadCSV = async (data) => {
    if (!data) return;

    data = data.map((record) => {
      const Id = record.su_survey_submitted_id ?? "";
      // For groups, use survey_id and default_report from the record; for individual surveys, use params
      const useSurveyId = record.su_survey_id || survey_id;
      const useReportId = record.default_report || report_id || '1';
      const Link = `${
        process.env.REACT_APP_USER_LINK || ""
      }/report/${useSurveyId}/${Id}/${useReportId}`;
      // const Date = record.su_add_date_time
      //   ? moment(record.su_add_date_time).format("YYYY/MM/DD")
      //   : "";
      const Date = record.su_add_date_time
        ? moment(record.su_add_date_time, "DD-MM-YYYY | hh:mm A").format(
            "YYYY/MM/DD"
          )
        : "";

      let Score = 0;
      if (record.su_survey_question_score) {
        Score = record.su_survey_question_score
          .split("#")
          .reduce(
            (sum, val) => sum + (isNaN(Number(val)) ? 0 : Number(val)),
            0
          );
      }

      const Stage = record?.maturity_stage ?? "";

      let Completed = record.su_is_completed;
      Completed = Completed ? (Number(Completed) === 1 ? "YES" : "NO") : "NO";

      let Response = "0/0";
      if (record.su_survey_response && record.su_survey_question_serial_no) {
        const responses = record.su_survey_response
          .split("#")
          .filter(Boolean).length;
        const serials =
          record.su_survey_question_serial_no.split("#").length - 1;
        Response = `${responses}/${serials}`;
      }
      // UTM (re-use your helper which already handles double-encoded JSON)
      const utmSource = getUTMInfo(record, "utm_source");
      const utmMedium = getUTMInfo(record, "utm_medium");
      const utmCampaign = getUTMInfo(record, "utm_campaign");

      const exportRow = {
        "Date of survey": String(Date),
        "Report link": String(Link),
      };

      dynamicLeadFields.forEach((field) => {
        exportRow[toSentenceCase(field.label)] = String(formatLeadFieldValue(record, field.key) ?? "");
      });

      exportRow["Marketing prefs."] = String(getMarketingPrefsValue(record));

      Object.assign(exportRow, {
        "No. of responses": String(Response),
        "Maturity level": record.maturity_stage ?? "",
        "Total score": Score,
        Completed: String(Completed),
        "Unique ID": String(Id),
        "UTM source": String(utmSource || ""),
        "UTM medium": String(utmMedium || ""),
        "UTM campaign": String(utmCampaign || ""),
      });

      return exportRow;
    });

    const exportHeaders = [
      "Date of survey",
      "Report link",
      ...dynamicLeadFields.map((field) => toSentenceCase(field.label)),
      "Marketing prefs.",
      "No. of responses",
      "Maturity level",
      "Total score",
      "Completed",
      "Unique ID",
      "UTM source",
      "UTM medium",
      "UTM campaign",
    ].filter((header, index, arr) => header && arr.indexOf(header) === index);

    const ws = XLSX.utils.json_to_sheet(data, { header: exportHeaders });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");

    const wbout = await XLSX.write(wb, {
      type: "array",
      bookType: "xlsx",
    });

    const blobData = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    FileSaver.saveAs(blobData, "Leads.xlsx");
  };

  const handleSearch = (searchValue) => {
    const payload = {
      search: searchValue,
    };
    
    if (group_id) {
      payload.group_id = group_id;
    } else {
      payload.survey_id = survey_id;
    }
    
    dispatch(getLeads(payload));
  };

  const filteredRows = useMemo(() => {
    if (!filterText) return leadsData ?? [];

    const q = filterText.toLowerCase();
    const contains = (v = "") => v.toString().toLowerCase().includes(q);

    return (leadsData ?? []).filter((row) => {
      // date reformatted exactly as rendered in the table
      const date = row.su_add_date_time
        ? moment(row.su_add_date_time, "DD-MM-YYYY | hh:mm A").format(
            "YYYY/MM/DD"
          )
        : "";

      const hasDynamicFieldMatch = dynamicLeadFields.some((field) =>
        contains(formatLeadFieldValue(row, field.key))
      );

      const hasMarketingPrefsMatch = contains(formatLeadFieldValue(row, "TANDC"));

      const ruleSummary =
        activeTab === "archived"
          ? formatArchivedRuleSummary(
              row.archived_rule_type,
              row.archived_rule_config
            )
          : "";

      return (
        contains(row.su_survey_submitted_id) ||
        contains(date) ||
        hasDynamicFieldMatch ||
        contains(getUTMInfo(row, "utm_source")) ||
        contains(getUTMInfo(row, "utm_medium")) ||
        contains(getUTMInfo(row, "utm_campaign")) ||
        hasMarketingPrefsMatch ||
        contains(ruleSummary)
      );
    });
  }, [dynamicLeadFields, filterText, leadsData, activeTab]);

  //Effects
  useEffect(() => {
    if (survey_id || group_id) {
      const payload = {
        search: "",
        archived: activeTab === "archived" ? 1 : 0,
      };

      if (group_id) {
        payload.group_id = group_id;
      } else {
        payload.survey_id = survey_id;
      }

      dispatch(getLeads(payload));

      // Only fetch stages if we have a survey_id (not for groups)
      if (survey_id) {
        dispatch(getStages({ survey_id: survey_id }));
      }
    }
  }, [survey_id, group_id, activeTab, dispatch]);

  useEffect(() => {
    const loadDynamicLeadFields = async () => {
      const uniqueSurveyIds = Array.from(
        new Set(
          (leadsData || [])
            .map((row) => row?.su_survey_id)
            .filter(Boolean)
        )
      );

      if (!uniqueSurveyIds.length && !survey_id) {
        setDynamicLeadFields([]);
        return;
      }

      const fallbackSurveyIds = uniqueSurveyIds.length ? uniqueSurveyIds : [survey_id];
      const responses = await Promise.all(
        fallbackSurveyIds.map((id) =>
          apiRequest({
            method: "GET",
            url: `/survey/getLeadGenVar?survey_id=${id}`,
          })
        )
      );

      const mergedMap = new Map();
      responses.forEach(({ response }) => {
        const fields = response?.data?.result || [];
        fields.forEach((field) => {
          if (!isDynamicLeadField(field)) return;

          const key = field.su_name;
          const fieldType = Number(field?.su_form_variable_type);
          const optionsMap = (fieldType === 3 || fieldType === 4)
            ? parseJsonSafe(field?.su_options, {})?.option || null
            : null;

          if (!mergedMap.has(key)) {
            mergedMap.set(key, {
              key,
              label: getTranslatedFieldLabel(field),
              order: Number(field?.su_order) || 9999,
              options: optionsMap,
            });
            return;
          }

          const existing = mergedMap.get(key);
          const fieldOrder = Number(field?.su_order) || 9999;
          if (fieldOrder < existing.order) {
            mergedMap.set(key, {
              ...existing,
              label: getTranslatedFieldLabel(field),
              order: fieldOrder,
              options: optionsMap || existing.options,
            });
          }
        });
      });

      const sortedFields = Array.from(mergedMap.values()).sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return sortString(a.label, b.label);
      });

      setDynamicLeadFields(sortedFields);
    };

    loadDynamicLeadFields();
  }, [leadsData, survey_id]);

  return (
    <>
      <div className="d-flex flex-column main-outer-wrapper">
        <SurveyListHeader />
        <div className="flex-grow-1 d-flex survey-main-wrapper">
          <SurveyListSidebar />
          <div className="flex-grow-1 dashboard-page-outer">
            <div className="dashboard-page-wrap">
              <div className="page-heading d-md-flex align-items-center justify-content-between mb-20 flex-wrap gap-2">
                <div className="head-lef">
                  <div className="survey-tabs-nav">
                    <button
                      type="button"
                      className={`survey-tab-link ${activeTab === "all" ? "active" : ""}`}
                      onClick={() => setActiveTab("all")}
                    >
                      {t("analytics.tab_all")}
                    </button>
                    <button
                      type="button"
                      className={`survey-tab-link ${activeTab === "archived" ? "active" : ""}`}
                      onClick={() => setActiveTab("archived")}
                    >
                      {t("analytics.tab_archived")}
                    </button>
                  </div>
                </div>

                <div className="right-wrap mt-2 mt-md-0 d-flex align-items-center gap-3">
                   {window.modulePermissions.M6.del == 1 && (
                  <button
                    type="button"
                    className="btn btn-bordered"
                    disabled={selectedRows.length === 0}
                    onClick={() => {
                      if (selectedRows.length > 0) {
                        // Show bulk delete confirmation modal
                        const modal = new window.bootstrap.Modal(document.getElementById('bulkDeleteModal'));
                        modal.show();
                      }
                    }}
                  >
                    {t("analytics.del3")}
                  </button>
                    )}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => downloadCSV(leadsData)}
                  >
                    <Svg name="excel" /> {t("survey.export")}
                  </button>
                </div>
                
              </div>
              <div className="table-wrapper-view">
                <div className="search-filter d-md-flex align-items-center surveyfilter">
                  <div className="flex-grow-1 search-filter-padding">
                    <SearchInput
                      className="custom-search-input"
                      type="def"
                      view="outlined"
                      color="primary"
                      placeholder={t("survey.search")}
                      fill={true}
                      onChange={(e) => setFilterText(e.target.value)}
                      onClear={() => setFilterText("")}
                    />
                  </div>
                </div>
              <div className="dashboard-cards-wraper row">
                <div className="col-lg-12">
                  <div className="min-100 pb-0">
                    <div className="dashboard-survey-list dashboard-leads-list">
                      <div className="data-table-wrap custom-data-table table-rsponsive">
                        <DataTable
                          columns={columns}
                          // data={leadsData ?? []}
                          data={filteredRows}
                          pagination
                          defaultSortFieldID={1}
                          customStyles={leadsTableCustomStyles}

                          selectableRows= {window?.modulePermissions?.M6?.del === 1}
                          selectableRowsHighlight
                          selectableRowsVisibleOnly   
                          onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                          clearSelectedRows={toggleCleared}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
              <div
                className="customvar-modal modal custom-modal fade deleteModal"
                id="deleteModal"
                tabIndex={-1}
                aria-labelledby="deleteModalLabel"
                aria-hidden="true"
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-body">
                      <div
                        className={
                          "formvawrapper delete-modal-wrap modal-setting-medium"
                        }
                      >
                        <div className={"formstyle p-0"}>
                          <div className="delete-body-wrap delete-language">
                            <div className="heading">
                              <h3>{t("analytics.del2")}</h3>
                              <p>{t("analytics.del1")}</p>
                            </div>

                            <div className="button-wrap d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-delete"
                                data-bs-dismiss="modal"
                                onClick={() => {
                                  if (selectedDeleteLead && (survey_id || group_id)) {
                                    const sid =
                                      selectedDeleteLead.su_survey_id || survey_id;
                                    dispatch(
                                      deleteLead(
                                        {
                                          su_survey_submitted_id:
                                            selectedDeleteLead.su_survey_submitted_id,
                                          su_survey_id: sid,
                                        },
                                        (success) => {
                                          if (success) {
                                            toast.success(
                                              "Lead deleted successfully"
                                            );
                                            const refreshPayload = {
                                              search: "",
                                              archived:
                                                activeTab === "archived" ? 1 : 0,
                                            };
                                            if (group_id) {
                                              refreshPayload.group_id = group_id;
                                            } else {
                                              refreshPayload.survey_id = survey_id;
                                            }
                                            dispatch(getLeads(refreshPayload));
                                          } else {
                                            toast.error(
                                              "Failed to delete lead. Please try again"
                                            );
                                          }
                                          setSelectedDeleteLead(null);
                                        }
                                      )
                                    );
                                  }
                                }}
                              >
                                {t("settings.roles.delete_action.delete")}
                              </button>

                              <button
                                type="button"
                                className="btn btn-bordered color-cancel ms-3"
                                data-bs-dismiss="modal"
                              >
                                {t("settings.roles.delete_action.cancel")}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bulk Delete Modal */}
              <div
                className="customvar-modal modal custom-modal fade"
                id="bulkDeleteModal"
                tabIndex={-1}
                aria-labelledby="bulkDeleteModalLabel"
                aria-hidden="true"
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-body">
                      <div
                        className={
                          "formvawrapper delete-modal-wrap modal-setting-medium"
                        }
                      >
                        <div className={"formstyle p-0"}>
                          <div className="delete-body-wrap delete-language">
                            <div className="heading">
                              <h3>{t("analytics.del3")}</h3>
                              <p>{t("survey.share.del1")} {selectedRows.length} {t("analytics.del4")}</p>
                            </div>

                            <div className="button-wrap d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-delete"
                                data-bs-dismiss="modal"
                                onClick={() => {
                                  if (selectedRows.length > 0 && survey_id) {
                                    // Call bulk delete API
                                    const leadIds = selectedRows.map(row => row.su_survey_submitted_id);
                                    dispatch(
                                      deleteBulkLeads(
                                        {
                                          lead_ids: leadIds,
                                          su_survey_id: survey_id,
                                        },
                                        (success) => {
                                          if (success) {
                                          toast.success(`${selectedRows.length} leads deleted successfully`);
                                          setSelectedRows([]);
                                          setToggleCleared(prev => !prev); 
                                          const payload = {
                                            search: "",
                                            archived:
                                              activeTab === "archived" ? 1 : 0,
                                          };
                                          if (group_id) {
                                            payload.group_id = group_id;
                                          } else {
                                            payload.survey_id = survey_id;
                                          }
                                          dispatch(getLeads(payload));
                                        } else {
                                          toast.error("Failed to delete leads. Please try again");
                                        }

                                        }
                                      )
                                    );
                                  }
                                }}
                              >
                            {t("analytics.del2")}
                              </button>

                              <button
                                type="button"
                                className="btn btn-bordered color-cancel ms-3"
                                data-bs-dismiss="modal"
                              >
                                {t('settings.team.action.delete.cancel')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
